import { http } from '@/api/http';
import type { PageEnvelope, Schemas } from '@/api/shared';

/**
 * Raw transport surface for the admin identifier-types feature. The view-model
 * orchestration (React Query hooks, mappers) lives in `./api.ts`.
 */

/** Server-side DTOs referenced by mappers + tests. */
export type IdentifierDefinitionDto = Schemas['IdentifierDefinition'];
/** @deprecated Use {@link IdentifierDefinitionDto}; alias kept for the few read-only call sites we haven't ported yet. */
export type IdentifierTypeDto = IdentifierDefinitionDto;
export type IdentifierDefinitionTypeCode = Schemas['IdentifierDefinitionType'];

/** Spring `Page<IdentifierDefinition>` envelope. */
export type IdentifierDefinitionPageDto = PageEnvelope<IdentifierDefinitionDto>;

export const adminIdentifierTypesEndpoints = {
  /**
   * GET /identifier-definitions — paginated catalog of admin-managed identifier
   * definitions for the current tenant. The Spring page envelope's `content` is
   * what every existing FE consumer treats as the array.
   */
  getIdentifierDefinitions: (
    params?: { page?: number; size?: number; active?: boolean; signal?: AbortSignal },
  ): Promise<IdentifierDefinitionPageDto> => {
    const search = new URLSearchParams();
    if (params?.page !== undefined) search.set('page', String(params.page));
    if (params?.size !== undefined) search.set('size', String(params.size));
    if (params?.active !== undefined) search.set('active', String(params.active));
    const query = search.toString();
    const path = query.length > 0 ? `/identifier-definitions?${query}` : '/identifier-definitions';
    return http.get<IdentifierDefinitionPageDto>(
      path,
      params?.signal !== undefined ? { signal: params.signal } : undefined,
    );
  },

  /**
   * POST /identifier-definitions — create a tenant-scoped definition. The BE
   * enforces single-primary: if `isPrimary: true`, it clears `isPrimary` on
   * every other row in the same tenant atomically.
   */
  createIdentifierDefinition: (
    body: IdentifierDefinitionDto,
    options?: { signal?: AbortSignal },
  ): Promise<IdentifierDefinitionDto> =>
    http.post<IdentifierDefinitionDto>('/identifier-definitions', body, options),

  /** PUT /identifier-definitions/{id} — replace the definition. Same primary-uniqueness rule as create. */
  updateIdentifierDefinition: (
    id: string,
    body: IdentifierDefinitionDto,
    options?: { signal?: AbortSignal },
  ): Promise<IdentifierDefinitionDto> =>
    http.put<IdentifierDefinitionDto>(
      `/identifier-definitions/${encodeURIComponent(id)}`,
      body,
      options,
    ),

  /**
   * DELETE /identifier-definitions/{id}. Returns 204; 409 if the definition is
   * still referenced by one or more persons.
   */
  deleteIdentifierDefinition: (id: string, options?: { signal?: AbortSignal }): Promise<undefined> =>
    http.delete<undefined>(`/identifier-definitions/${encodeURIComponent(id)}`, options),
};
